import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useRef } from 'react';
import { SimpleDatePicker } from '@/components/DatePicker/SimpleDatePicker';
import { FillScreen } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { EmptyState } from '../../-components/EmptyState';
import OrganizationToolbar from '../../-components/OrganizationToolbar';
import { HierarchyView } from '../../-components/views/HierarchyView';
import { TableView } from '../../-components/views/TableView';
import { TreeView } from '../../-components/views/TreeView';
import { ViewMode } from '../../-constant';
import { ORG_CHART_MARKS, ORG_CHART_MEASURES, markOrgChartPerf, measureOrgChartPerf } from './-perf/marks';
import { OrganizationChartPerfOverlay } from './-perf/PerfOverlay';
import useOrganizationChart from './-useOrganizationChart';
import { SearchSchema } from './-zod-schema';
export const Route = createFileRoute('/_staff/organization/_list/chart/')({
  component: OrganizationPage,
  validateSearch: zodValidator(SearchSchema),
  loaderDeps: ({
    search
  }) => ({
    search
  })
});
function OrganizationPage() {
  const navigate = useNavigate();
  const {
    baseDate
  } = useSearch({
    strict: false
  });
  const {
    viewMode,
    isCompactView,
    isMiniMapVisible,
    data,
    hasQuery,
    emptyVariant,
    isLoading,
    isEmpty,
    toolbarProps,
    dateProps,
    hierarchyLabels,
    hierarchyViewRef,
    treeViewRef,
    tableViewRef,
    handlers,
    baseDateISO,
    focusedNodeId,
    initialCollapsed,
    onCollapsedChange
  } = useOrganizationChart();

  // 検索適用 / 解除のタイミングでビューを再マウントし、
  // - React Flow ビューの collapsed state をリセット（マッチがあるノードを必ず表示する）
  // - TableView のページネーションを 1 ページ目に戻す
  const viewKey = hasQuery ? 'filtered' : 'default';

  // first paint（skeleton 取得後に最初にチャートを描画した瞬間）を 1 度だけ計測する
  // （ADR-451 Phase 0）。本番ビルドでは marks 側が no-op。
  const firstPaintMarkedRef = useRef(false);
  useEffect(() => {
    // 新しいロードセッション（baseDate 変更等）が始まったらリセットして再計測する
    if (isLoading) {
      firstPaintMarkedRef.current = false;
      return;
    }
    if (firstPaintMarkedRef.current || !data || data.length === 0) {
      return;
    }
    firstPaintMarkedRef.current = true;
    markOrgChartPerf(ORG_CHART_MARKS.firstPaint);
    measureOrgChartPerf(ORG_CHART_MEASURES.skeletonToFirstPaint, ORG_CHART_MARKS.skeletonLoaded, ORG_CHART_MARKS.firstPaint);
  }, [isLoading, data]);
  return <FillScreen className="flex flex-col gap-3 px-12 py-5">
      <div className="w-fit bg-neutral-fill rounded-md">
        <SimpleDatePicker value={dateProps.date ? new Date(dateProps.date) : undefined} onChange={dateProps.onDateChange} />
      </div>
      <OrganizationToolbar {...toolbarProps} />
      {isLoading ? <ViewportLoader /> : isEmpty ? <EmptyState variant={emptyVariant} /> : <>
          {viewMode === ViewMode.HIERARCHY && <HierarchyView key={viewKey} organizations={data ?? []} handlers={handlers} isCompact={isCompactView} showMiniMap={isMiniMapVisible} focusedNodeId={focusedNodeId} initialCollapsed={initialCollapsed} onCollapsedChange={onCollapsedChange} ref={hierarchyViewRef} />}
          {viewMode === ViewMode.TREE && <TreeView key={viewKey} organizations={data ?? []} isCompact={isCompactView} hierarchyLabels={hierarchyLabels} handlers={handlers} showMiniMap={isMiniMapVisible} focusedNodeId={focusedNodeId} initialCollapsed={initialCollapsed} onCollapsedChange={onCollapsedChange} ref={treeViewRef} />}
          {viewMode === ViewMode.TABLE && <TableView key={viewKey} organizations={data ?? []} hierarchyLabels={hierarchyLabels} isCompactView={isCompactView} baseDate={baseDateISO} onRowClick={row => {
        navigate({
          to: '/organization/$id',
          params: {
            id: row.id
          },
          search: {
            baseDate
          }
        });
      }} ref={tableViewRef} />}
        </>}
      <OrganizationChartPerfOverlay nodeCount={data?.length} />
    </FillScreen>;
}